// 2026-07-14 - FormuLearning - RLD → Step3 값 전파 헬퍼 (이슈 #33 G4·Q-260714-06)
// 2026-09-24 수정 - 이슈 #75(G4): **대조약 선택 시 X 재구성**(`M901-14`, 프로세스 v1.23 `BR-S3-10`).
//   종전의 두 경로(자동 병합 `mergeXVariables` = 주성분 행만 교체·첨가제 보존 / 수동 `Pull from RLD`
//   = 덮어쓰기)를 하나로 합친다 — **X 가 비었으면 채우고, 있으면 물어서 갈아끼우거나 그대로 둔다.**
//   병합은 첨가제가 옛 대조약 것으로 남아 "바꿨는데 미적용" 으로 보였다(이슈 #75 DoD).
//   주성분 행 삭제 시 승계(`removeXRow`, `Q-260916-06` ③)도 여기 둔다.
// 파일 위치: frontend/src/features/step3/rldToStep3.ts
// 주성분은 x_variables에 `is_active:true` 행으로 저장한다 — 뜻은 «대조약 기준 원래 주성분»이며
// 잠금이 아니다(`BR-S3-11`). 순수 계산부(build*/diff/remove)와 IO부(applyRldToStep3)를 나눈다.

import { getStep3, updateStep3 } from '../../api/step3'
import type { Step3Data, XVariable } from '../../api/step3'
import { pullDefault, positiveNumber, numericEquals } from '../../lib/pullDefault'

export const ACTIVE_FUNCTION = 'Active (API)'

interface MainIngredient {
  name?: string
  amount_mg?: number | null
}

const round2 = (n: number) => Math.round(n * 100) / 100

// RLD main_ingredients(is_active 성분) → 주성분 fixed 행(`is_active`, 편집 가능 — BR-S3-11).
// 용량% = amount_mg / totalWeight * 100. 총중량 미확정이면 0(후속 stale 규약 대상, Q-260714-04).
export function buildActiveRows(rld: Record<string, unknown>, totalWeight: number | null): XVariable[] {
  const mains = (rld.main_ingredients as MainIngredient[] | undefined) ?? []
  return mains
    .filter((m) => m && m.name)
    .map((m, i) => {
      const amt = typeof m.amount_mg === 'number' && Number.isFinite(m.amount_mg) ? m.amount_mg : null
      const pct = amt != null && totalWeight && totalWeight > 0 ? round2((amt / totalWeight) * 100) : 0
      return {
        excipient: m.name as string,
        function: ACTIVE_FUNCTION,
        mode: 'fixed' as const,
        min_pct: pct,
        max_pct: pct,
        sort_order: i,
        is_active: true,
      }
    })
}

// RLD excipients(콤마구분 문자열) → 첨가제 selected 행(startOrder부터 정렬).
export function buildExcipientRows(rld: Record<string, unknown>, startOrder: number): XVariable[] {
  const excStr = rld.excipients as string | undefined
  const names = excStr ? excStr.split(',').map((s) => s.trim()).filter(Boolean) : []
  return names.map((name, i) => ({
    excipient: name,
    function: '',
    mode: 'selected' as const,
    min_pct: 0,
    max_pct: 0,
    sort_order: startOrder + i,
  }))
}

// 선택 대조약 기준 X 표 전체(주성분 행 + 첨가제 행).
export function buildRldXRows(rld: Record<string, unknown>, totalWeight: number | null): XVariable[] {
  const activeRows = buildActiveRows(rld, totalWeight)
  const excipientRows = buildExcipientRows(rld, activeRows.length)
  return [...activeRows, ...excipientRows].map((r, i) => ({ ...r, sort_order: i }))
}

// 행 삭제 — 지운 행이 `is_active` 면 목록에서 그 **다음** `Active (API)` 행이 승계한다.
// 다음 행이 없으면 승계하지 않는다(`Q-260916-06` ③). 수기로 추가한 API 행은 그 전까지
// `is_active` 가 아니다(②).
export function removeXRow(rows: XVariable[], idx: number): XVariable[] {
  const removed = rows[idx]
  const heir = removed?.is_active
    ? rows.findIndex((r, i) => i > idx && !r.is_active && r.function === ACTIVE_FUNCTION)
    : -1
  return rows
    .map((r, i) => (i === heir ? { ...r, is_active: true } : r))
    .filter((_, i) => i !== idx)
    .map((r, i) => ({ ...r, sort_order: i }))
}

// 갈아끼우면 사라지는 사용자 입력(확인 문구 재료).
// removed: 새 대조약에 없어 표에서 사라지는 행 / edited: 이름은 남지만 대조약 값으로 되돌아가는 행.
export interface XReplaceLoss {
  removed: string[]
  edited: string[]
}

const keyOf = (r: XVariable) => r.excipient.trim()

export function diffXReplace(existing: XVariable[], next: XVariable[]): XReplaceLoss {
  const byName = new Map(next.map((r) => [keyOf(r), r]))
  const removed: string[] = []
  const edited: string[] = []
  for (const row of existing ?? []) {
    if (!keyOf(row)) continue // 이름 없는 빈 행은 소실로 보지 않는다
    const n = byName.get(keyOf(row))
    if (!n) {
      removed.push(row.excipient)
    } else if (
      row.function !== n.function ||
      row.mode !== n.mode ||
      row.min_pct !== n.min_pct ||
      row.max_pct !== n.max_pct
    ) {
      edited.push(row.excipient)
    }
  }
  return { removed, edited }
}

// 확인 문구(BR-S3-10 ②). 무엇이 사라지고 바뀌는지 이름·값까지 보여 사용자가 판단하게 한다.
export function xReplaceMessage(loss: XReplaceLoss, weightFrom: number | null, weightTo: number | null): string {
  const lines = [
    '입력된 X 변수가 있습니다.',
    '선택한 대조약 기준으로 주성분과 첨가제 목록을 갱신하시겠습니까?',
    '',
  ]
  if (loss.removed.length) {
    lines.push(`· 삭제되는 행 (${loss.removed.length}건): ${loss.removed.join(', ')}`)
  }
  if (loss.edited.length) {
    lines.push(`· 대조약 값으로 되돌아가는 행 (${loss.edited.length}건): ${loss.edited.join(', ')}`)
  }
  if (weightTo != null && weightFrom !== weightTo) {
    lines.push(`· 총중량: ${weightFrom ?? '—'} mg → ${weightTo} mg`)
  }
  lines.push('', '[확인] 갱신 · [취소] 대조약 선택만 하고 X 변수는 그대로 둡니다.')
  return lines.join('\n')
}

// 대조약 선택을 Step 3 에 반영한다(BR-S3-10). 바뀐 게 없거나 사용자가 거절하면 null.
// - X 가 비었으면 묻지 않고 채운다. 총중량은 표준 pull 규칙으로 수동값을 보호한다.
// - X 가 있으면 `confirm` 으로 묻는다. 확인 → X 를 갈아끼우고 총중량도 대조약 값으로(수동 해제),
//   취소 → 아무것도 바꾸지 않는다(대조약 선택은 호출부가 이미 저장했다).
export async function applyRldToStep3(
  projectId: string,
  rld: Record<string, unknown>,
  confirm: (message: string) => boolean,
): Promise<Step3Data | null> {
  const s3 = await getStep3(projectId)
  const existing = s3.x_variables ?? []
  const w = round2(parseFloat(String(rld.weight_mg ?? '')))
  const rldWeight = Number.isFinite(w) && w > 0 ? w : null
  const patch: Partial<Step3Data> = {}

  if (existing.length === 0) {
    const nextWeight = pullDefault({
      current: s3.total_weight_mg,
      sourceValue: rldWeight,
      isManual: s3.total_weight_manual,
      isValid: positiveNumber,
      isEqual: numericEquals(0.005),
    })
    if (nextWeight != null) {
      patch.total_weight_mg = nextWeight
      patch.total_weight_manual = false
    }
    const nextX = buildRldXRows(rld, nextWeight ?? s3.total_weight_mg)
    if (nextX.length) patch.x_variables = nextX
  } else {
    const weight = rldWeight ?? s3.total_weight_mg
    const nextX = buildRldXRows(rld, weight)
    if (nextX.length === 0) return null // 대조약에 가져올 주성분·첨가제가 없다 — 기존 X 를 비우지 않는다
    if (!confirm(xReplaceMessage(diffXReplace(existing, nextX), s3.total_weight_mg, rldWeight))) return null
    patch.x_variables = nextX
    if (rldWeight != null) {
      patch.total_weight_mg = rldWeight
      patch.total_weight_manual = false
    }
  }

  if (Object.keys(patch).length === 0) return null
  return updateStep3(projectId, patch)
}
